import { FileClock } from "lucide-react";
import { useState } from "react";
import type { SocialReportDraft } from "@/shared/api/api-client";
import { formatWindowFromCriteria } from "@/shared/utils/date-range";
import { criteriaChips, ReportCriteriaChips } from "@/features/reports/ReportCriteriaChips";

import { formatCena, formatInteger } from "@/shared/utils/format";
/** Quantos rascunhos o banner mostra antes de pedir pra abrir o resto.
 *  O servidor já corta em 20 (`_PENDING_DRAFTS_CAP`); este é o corte
 *  visual, pra um dia de garimpo não virar uma parede de rascunhos em
 *  cima da tela. */
const VISIBLE = 5;


function daysUntil(iso: string): number {
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
}

/**
 * Aviso de rascunho não salvo. Existe porque o cálculo é caro e o
 * rascunho tem prazo: sem esse lembrete, a pessoa geraria um relatório,
 * sairia da tela e perderia o trabalho sem nunca saber que ele estava
 * ali esperando.
 *
 * Usa `--accent`, não âmbar: as cores semânticas (verde/coral/âmbar)
 * são reservadas à polaridade em toda a interface, e um aviso amarelo
 * aqui competiria com o significado delas na lista logo abaixo.
 */
export function PendingDraftsBanner({
  drafts,
  activeDraftId,
  onOpen,
  onDiscard
}: {
  drafts: SocialReportDraft[];
  activeDraftId: number | null;
  onOpen: (draftId: number) => void;
  onDiscard: (draft: SocialReportDraft) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!drafts.length) return null;

  const visible = expanded ? drafts : drafts.slice(0, VISIBLE);

  return (
    <section className="card report-draft-banner" aria-label="Rascunhos não salvos">
      <p className="report-draft-banner-head">
        <FileClock size={16} aria-hidden="true"/>
        {drafts.length === 1
          ? "Você tem 1 rascunho não salvo"
          : `Você tem ${drafts.length} rascunhos não salvos`}
      </p>
      <ul>
        {visible.map(draft => {
          const remaining = daysUntil(draft.expires_at);
          return (
            <li key={draft.id}>
              <div className="report-draft-banner-info">
                <strong>
                  {formatWindowFromCriteria(
                    draft.generation_criteria,
                    draft.window_start,
                    draft.window_end
                  )}
                  {draft.brand_name && ` · ${draft.brand_name}`}
                </strong>
                {/* Sem isto, dois rascunhos que diferem só na busca
                    seriam duas linhas idênticas — e desde que a trava
                    compara o recorte inteiro, essa é a situação
                    normal de quem está garimpando o corte certo. */}
                <ReportCriteriaChips
                  items={criteriaChips(
                    draft.generation_criteria,
                    draft.generation_criteria_labels
                  )}
                />
                <span>
                  ICena médio {formatCena(draft.aggregates.avg_icena)} ·{" "}
                  {formatInteger(draft.aggregates.post_count)} publicações ·{" "}
                  {remaining === 0
                    ? "expira hoje"
                    : `expira em ${remaining} dia${remaining > 1 ? "s" : ""}`}
                </span>
              </div>
              <div className="report-draft-banner-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onOpen(draft.id)}
                  disabled={draft.id === activeDraftId}
                >
                  {draft.id === activeDraftId ? "Aberto" : "Abrir"}
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => onDiscard(draft)}
                >
                  Descartar
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      {drafts.length > VISIBLE && !expanded && (
        <button type="button" className="text-button" onClick={() => setExpanded(true)}>
          Mostrar todos ({drafts.length})
        </button>
      )}
    </section>
  );
}
